import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import {
  School as SchoolIcon,
  LocalShipping as DeliveryIcon,
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { projectTemplates } from './mockData';

export default function ProjectTemplateModal({ open, onClose }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    jobNumber: '',
    clientName: '',
    pmName: '',
    requiresLift: false,
    liftHeight: '',
    liftProvider: 'Blades AV',
    budgetHours: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    tasks: [],
  });
  const [showForm, setShowForm] = useState(false);

  const templateIcons = {
    'school-install': <SchoolIcon color="primary" />,
    'quick-delivery': <DeliveryIcon color="success" />,
    'multi-site': <BusinessIcon color="warning" />,
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      requiresLift: template.requiresLift,
      liftHeight: template.defaultLiftHeight || '',
      budgetHours: template.defaultBudgetHours,
      tasks: template.tasks,
    });
    setShowForm(true);
  };

  const handleCreateProject = () => {
    enqueueSnackbar(`Project ${formData.jobNumber} created from ${selectedTemplate.name} template!`, {
      variant: 'success',
    });
    handleClose();
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setShowForm(false);
    setFormData({
      jobNumber: '',
      clientName: '',
      pmName: '',
      requiresLift: false,
      liftHeight: '',
      liftProvider: 'Blades AV',
      budgetHours: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      tasks: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {!showForm ? (
        <>
          <DialogTitle>
            Select Project Template
            <Typography variant="body2" color="text.secondary">
              Templates auto-populate lift requirements and standard tasks
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Key Feature:</strong> No more forgetting lift requirements! Templates ensure consistent project setup.
            </Alert>
            <List>
              {projectTemplates.map((template) => (
                <ListItem
                  key={template.id}
                  button
                  onClick={() => handleSelectTemplate(template)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon>{templateIcons[template.id]}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {template.name}
                        {template.requiresLift && (
                          <Chip label="Lift Required" size="small" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          {template.description}
                        </Typography>
                        <br />
                        <Typography variant="caption" component="span" color="primary">
                          {template.defaultBudgetHours} hours • {template.tasks.length} standard tasks
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            New Project from {selectedTemplate.name} Template
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  label="Job Number"
                  value={formData.jobNumber}
                  onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
                  fullWidth
                  placeholder="J2024-XXX"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Client Name"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Project Manager"
                  value={formData.pmName}
                  onChange={(e) => setFormData({ ...formData, pmName: e.target.value })}
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="">Select PM</option>
                  <option value="Matt">Matt</option>
                  <option value="Brandon">Brandon</option>
                  <option value="Clayton">Clayton</option>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Budget Hours"
                  type="number"
                  value={formData.budgetHours}
                  onChange={(e) => setFormData({ ...formData, budgetHours: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: <Typography variant="caption">hours</Typography>,
                  }}
                />
              </Grid>

              {/* Lift Requirements Section - PRE-FILLED FROM TEMPLATE */}
              {selectedTemplate.requiresLift && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<CheckIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                      Lift Requirements (Auto-populated from template)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="Lift Height"
                          value={formData.liftHeight}
                          onChange={(e) => setFormData({ ...formData, liftHeight: e.target.value })}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Lift Provider"
                          value={formData.liftProvider}
                          onChange={(e) => setFormData({ ...formData, liftProvider: e.target.value })}
                          fullWidth
                          size="small"
                          select
                          SelectProps={{ native: true }}
                        >
                          <option value="Blades AV">Blades AV</option>
                          <option value="Customer">Customer</option>
                          <option value="Third Party">Third Party</option>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>
              )}

              <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Standard Tasks - PRE-FILLED FROM TEMPLATE */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Standard Tasks (Auto-populated from template)
                </Typography>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  {formData.tasks.map((task, index) => (
                    <FormControlLabel
                      key={index}
                      control={<Checkbox defaultChecked />}
                      label={task}
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  These tasks will be automatically added to the project checklist
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleCreateProject}
              disabled={!formData.jobNumber || !formData.clientName || !formData.pmName}
            >
              Create Project
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}